import React, { useEffect, useMemo, useState } from 'react';
import Select, { ValueType } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import Modal from '../../../../ui/Model';
import Button, { ButtonType } from '../../../../shared/components/core-ui/button/button.component';
import { LeadConfigModel } from '../../../../shared/models/lead.model';
import { detailFieldFor, DetailFieldConfig, LookupSource } from './lead-detail-fields.config';
import { MarketingChannel } from '../../../../shared/interfaces/attribution.interface';
import { AreaUiModel } from '../../../../shared/ui-models/area.ui-model';
import { UnitTypeGroupUiModel } from '../../../../shared/ui-models/unit-type-group.ui-model';
import { ErrorInfo, isErrorInfo } from '../../../../shared/interfaces/error-info.interface';
import {
  getAllAreas,
  getAllTypeOfUnit,
  getAllUnitTypeGroups
} from '../../../../shared/services/data/lookups.data';

const selectMenuPortalStyles = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 })
};

const LABEL = 'mb-1 block text-sm font-gilroy-medium text-neutral-400';
const INPUT = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm';

interface Option {
  value: string;
  label: string;
}

const titleCase = (value: string): string =>
  (value || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());

const toOptions = (values: string[]): Option[] =>
  values.map((value: string) => ({ value, label: titleCase(value) }));

interface CreateLeadModalProps {
  show: boolean;
  config: LeadConfigModel;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => void;
}

export const CreateLeadModal: React.FC<CreateLeadModalProps> = ({
  show,
  config,
  onClose,
  onSubmit
}) => {
  const [vertical, setVertical] = useState('');
  const [source, setSource] = useState('admin');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [marketingChannel, setMarketingChannel] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [details, setDetails] = useState<Record<string, string>>({});
  const [lookups, setLookups] = useState<Record<LookupSource, string[]>>({
    areaNames: [],
    unitTypeGroups: [],
    typeOfUnits: []
  });

  /**
   * The lists this form offers instead of asking people to type from memory.
   *
   * Fetched once when the modal opens rather than per vertical: the same three
   * cover all of them, and a picker that populates after the operator has
   * already typed is worse than one that was there from the start.
   */
  useEffect(() => {
    if (!show) return;
    Promise.all([
      getAllAreas('CreateLeadModal'),
      getAllUnitTypeGroups('CreateLeadModal'),
      getAllTypeOfUnit('CreateLeadModal')
    ]).then(([areas, groups, types]) => {
      setLookups({
        // `areaName` stores the name, not an id — the lead service records what
        // the person said the area was, which is not always one of ours.
        areaNames: isErrorInfo(areas as ErrorInfo)
          ? []
          : (areas as AreaUiModel[]).map((a: AreaUiModel) => a.name),
        unitTypeGroups: isErrorInfo(groups as ErrorInfo)
          ? []
          : (groups as UnitTypeGroupUiModel[]).map((g: UnitTypeGroupUiModel) => g.name),
        typeOfUnits: isErrorInfo(types as ErrorInfo)
          ? []
          : (types as { name: string }[]).map((t: { name: string }) => t.name)
      });
    });
  }, [show]);

  useEffect(() => {
    setVertical('');
    setSource('admin');
    setName('');
    setPhone('');
    setEmail('');
    setMessage('');
    setMarketingChannel('');
    setUtmCampaign('');
    setDetails({});
  }, [show]);

  const verticalOptions = useMemo(() => toOptions(config.verticals ?? []), [config]);
  const sourceOptions = useMemo(() => toOptions(config.sources ?? []), [config]);

  /**
   * Closed, unlike the detail lookups below.
   *
   * Attribution reporting groups by this, so a typed-in channel is a new row in
   * every dashboard rather than a new option. `unknown` is in the list for the
   * leads that genuinely have no attribution.
   */
  const channelOptions: Option[] = useMemo(
    () =>
      Object.values(MarketingChannel).map((value: string) => ({ value, label: titleCase(value) })),
    []
  );

  /**
   * Which detail keys this vertical accepts, from the service.
   *
   * Not a local list: the service rejects an unknown key outright rather than
   * dropping it, so a hardcoded copy that drifts turns into a 400 nobody can
   * act on. Changing the vertical clears details for the same reason — keys
   * from the old vertical would be rejected.
   */
  const detailKeys: string[] = vertical ? config.detailKeysByVertical?.[vertical] ?? [] : [];

  // The only hard rules the service enforces on create: a vertical, a source,
  // and at least one way to contact the person. Mirrored here so a known-bad
  // submit is a disabled button rather than a 400.
  const hasContact = phone.trim().length > 0 || email.trim().length > 0;
  const canSubmit = vertical.length > 0 && source.length > 0 && hasContact;

  const setDetail = (key: string, value: string) =>
    setDetails((current) => ({ ...current, [key]: value }));

  const buildDetails = (): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    detailKeys.forEach((key: string) => {
      const raw = (details[key] ?? '').trim();
      if (!raw) return;
      const field: DetailFieldConfig = detailFieldFor(key);
      if (field.kind === 'number') {
        const n = Number(raw);
        if (!Number.isNaN(n)) out[key] = n;
        return;
      }
      if (field.kind === 'list') {
        const items = raw
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
        if (items.length) out[key] = items;
        return;
      }
      out[key] = raw;
    });
    return out;
  };

  const buildPayload = (): Record<string, unknown> => {
    const payload: Record<string, unknown> = { vertical, source };
    if (name.trim()) payload.name = name.trim();
    if (phone.trim()) payload.phone = phone.trim();
    if (email.trim()) payload.email = email.trim();
    if (message.trim()) payload.message = message.trim();
    if (marketingChannel.trim()) payload.marketingChannel = marketingChannel.trim();
    if (utmCampaign.trim()) payload.utm_campaign = utmCampaign.trim();

    const built = buildDetails();
    if (Object.keys(built).length) payload.details = built;
    return payload;
  };

  const renderDetail = (key: string) => {
    const field: DetailFieldConfig = detailFieldFor(key);

    if (field.kind === 'lookup' || field.kind === 'list') {
      const isMulti = field.kind === 'list';
      const options: Option[] = (field.source ? lookups[field.source] : []).map(
        (value: string) => ({ value, label: value })
      );
      const raw = details[key] ?? '';
      const currentMulti: Option[] = raw
        ? raw.split(',').map((v) => ({ value: v.trim(), label: v.trim() }))
        : [];
      const currentSingle: Option | null = raw ? { value: raw, label: raw } : null;

      return (
        <div key={key}>
          <label className={LABEL}>{field.label}</label>
          {/*
            Creatable, not a plain Select. These lists describe our catalogue,
            and a lead records what the person actually said — an owner naming
            an area or a unit type we do not have yet is a real lead, not a
            typo. Offering the list without allowing anything else would force
            whoever takes the call to pick something untrue.
          */}
          <CreatableSelect
            isMulti={isMulti}
            isClearable
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={selectMenuPortalStyles}
            classNamePrefix="react-select"
            options={options}
            value={isMulti ? currentMulti : currentSingle}
            placeholder={options.length ? 'Choose or type' : 'Type a value'}
            formatCreateLabel={(input: string) => `Use "${input}"`}
            noOptionsMessage={() => 'Type a value and press enter'}
            onChange={(value: ValueType<Option, boolean>) => {
              if (isMulti) {
                setDetail(key, ((value as Option[]) || []).map((o) => o.value).join(', '));
              } else {
                setDetail(key, value ? (value as Option).value : '');
              }
            }}
          />
          {field.hint ? <p className="mt-1 text-xs text-neutral-300">{field.hint}</p> : null}
        </div>
      );
    }

    return (
      <div key={key}>
        <label className={LABEL}>{field.label}</label>
        <input
          className={INPUT}
          type={field.kind === 'date' ? 'date' : field.kind === 'number' ? 'number' : 'text'}
          value={details[key] ?? ''}
          onChange={(event) => setDetail(key, event.target.value)}
        />
        {field.hint ? <p className="mt-1 text-xs text-neutral-300">{field.hint}</p> : null}
      </div>
    );
  };

  return (
    <Modal
      isOpen={show}
      onClose={onClose}
      containerClassName="lg:h-fit lg:max-h-[88vh] lg:w-[760px]"
      headerContent={
        <div className="text-center font-gilroy-bold text-2xl leading-[100%] text-neutral-500">
          Create lead
        </div>
      }
      footerContent={
        <div className="flex justify-end gap-4">
          <Button type={ButtonType.gray} onClick={onClose}>
            Cancel
          </Button>
          <Button
            type={ButtonType.primary}
            disabled={!canSubmit}
            tracking={{
              event: 'record_saved',
              entity: 'lead',
              action: 'create',
              outcome: 'success'
            }}
            onClick={() => onSubmit(buildPayload())}
          >
            Create lead
          </Button>
        </div>
      }
    >
      <div className="flex w-full flex-col gap-5">
        <section>
          <h3 className="mb-2 font-gilroy-bold text-sm text-neutral-500">Required</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={LABEL}>Vertical *</label>
              <Select
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectMenuPortalStyles}
                classNamePrefix="react-select"
                options={verticalOptions}
                placeholder="Which funnel is this?"
                value={verticalOptions.find((o: Option) => o.value === vertical) ?? null}
                onChange={(option: ValueType<Option, false>) => {
                  setVertical(option ? (option as Option).value : '');
                  // Keys from the previous vertical would be rejected outright.
                  setDetails({});
                }}
              />
            </div>

            <div>
              <label className={LABEL}>Source *</label>
              <Select
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectMenuPortalStyles}
                classNamePrefix="react-select"
                options={sourceOptions}
                value={sourceOptions.find((o: Option) => o.value === source) ?? null}
                onChange={(option: ValueType<Option, false>) =>
                  setSource(option ? (option as Option).value : '')
                }
              />
            </div>

            <div>
              <label className={LABEL}>Phone</label>
              <input
                className={INPUT}
                value={phone}
                placeholder="01xxxxxxxxx"
                onChange={(event) => setPhone(event.target.value)}
              />
            </div>

            <div>
              <label className={LABEL}>Email</label>
              <input
                className={INPUT}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
          </div>
          {!hasContact ? (
            <p className="mt-2 rounded-xl bg-creams-300 px-3 py-2 text-xs text-creams-900">
              A lead needs at least a phone number or an email address — there is no way to work one
              without either.
            </p>
          ) : null}
        </section>

        <section>
          <h3 className="mb-2 font-gilroy-bold text-sm text-neutral-500">Optional</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={LABEL}>Name</label>
              <input className={INPUT} value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className={LABEL}>Marketing channel</label>
              <Select
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectMenuPortalStyles}
                classNamePrefix="react-select"
                options={channelOptions}
                placeholder="Unknown"
                value={channelOptions.find((o: Option) => o.value === marketingChannel) ?? null}
                onChange={(option: ValueType<Option, false>) =>
                  setMarketingChannel(option ? (option as Option).value : '')
                }
              />
            </div>
            <div>
              <label className={LABEL}>Campaign</label>
              <input
                className={INPUT}
                value={utmCampaign}
                onChange={(e) => setUtmCampaign(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-3">
            <label className={LABEL}>Message</label>
            <textarea
              className={INPUT}
              rows={2}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
        </section>

        <section>
          <h3 className="mb-1 font-gilroy-bold text-sm text-neutral-500">
            {vertical ? `${titleCase(vertical)} details` : 'Funnel details'}
          </h3>
          {!vertical ? (
            <p className="text-xs text-neutral-400">Pick a vertical to see its fields.</p>
          ) : detailKeys.length === 0 ? (
            <p className="text-xs text-neutral-400">This vertical has no extra fields.</p>
          ) : (
            <>
              <p className="mb-2 text-xs text-neutral-400">
                All optional. Each vertical accepts only its own fields — the service rejects the
                rest rather than dropping them.
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {detailKeys.map(renderDetail)}
              </div>
            </>
          )}
        </section>
      </div>
    </Modal>
  );
};
